"use client";

import { useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { cancelRun, createAndRun, runSearch } from "@/lib/api/generated";
import type { SearchWithRunResponseSchema } from "@/lib/api/model";
import { projectSearchesQueryKey, savedSearchQueryKey } from "@/hooks/use-search";
import type { SearchDefinition } from "@/hooks/use-search-definition-draft";
import { useSearchRunsStore } from "@/stores/search-runs-store";

/**
 * Save-is-Run mutations. Both `runExisting` and `createAndRun` return the
 * `SearchWithRunResponseSchema` for the (possibly new) search; on success the
 * runs store is hydrated from `latest_run` so the UI flips to the running
 * phase immediately, the saved-search cache entry is replaced with the fresh
 * payload, and the project's search list is invalidated so the new / re-run
 * search surfaces in the sidebar. `cancel` invalidates the saved-search
 * payload and the project list on success so the store converges on
 * `cancelled` even if the stream's `run.cancelled` event is missed.
 *
 * Errors are the caller's to surface (they know whether it was a run, a
 * retry or a new search), so the mutations opt out of the query client's
 * generic "Something went wrong" toast. Quota 429s are still toasted
 * centrally; callers skip those to avoid a duplicate.
 */
const OWN_ERROR_TOAST = { suppressGlobalErrorToast: true } as const;

export function useRunSearch() {
  const qc = useQueryClient();
  const hydrate = useSearchRunsStore((s) => s.hydrateSearch);
  const trackOwnRun = useSearchRunsStore((s) => s.trackOwnRun);

  const onSuccess = useCallback(
    (data: SearchWithRunResponseSchema) => {
      if (data.latest_run) trackOwnRun(data.search.id);
      hydrate({ ...data.search, latest_run: data.latest_run ?? null });
      qc.setQueryData(savedSearchQueryKey(data.search.id), data);
      void qc.invalidateQueries({
        queryKey: projectSearchesQueryKey(data.search.project_id),
      });
    },
    [hydrate, trackOwnRun, qc],
  );

  const existing = useMutation({
    mutationFn: (v: {
      searchId: string;
      definition: SearchDefinition;
      name?: string;
    }) =>
      runSearch(v.searchId, { definition: v.definition, name: v.name ?? null }),
    onSuccess,
    meta: OWN_ERROR_TOAST,
  });
  const create = useMutation({
    mutationFn: (v: {
      projectId: string;
      definition: SearchDefinition;
      name?: string;
    }) =>
      createAndRun(v.projectId, {
        definition: v.definition,
        name: v.name ?? null,
      }),
    onSuccess,
    meta: OWN_ERROR_TOAST,
  });
  const cancel = useMutation({
    mutationFn: (v: { searchId: string; projectId: string }) =>
      cancelRun(v.searchId),
    meta: OWN_ERROR_TOAST,
    onSuccess: (_data, v) => {
      void qc.invalidateQueries({ queryKey: savedSearchQueryKey(v.searchId) });
      void qc.invalidateQueries({
        queryKey: projectSearchesQueryKey(v.projectId),
      });
    },
  });

  return {
    runExisting: existing.mutateAsync,
    createAndRun: create.mutateAsync,
    cancel: cancel.mutateAsync,
    isPending: existing.isPending || create.isPending,
  };
}
